import { Boxes, Link2, MoreVertical, Pencil, PlusCircle, Sliders, Trash2 } from 'lucide-react';

import type { Medicine } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ExpiryBadge, StockBadge } from '@/features/inventory/StockBadge';
import { money } from '@/lib/utils';

interface MedicineRowProps {
  medicine: Medicine;
  onEdit: () => void;
  onRestock: () => void;
  onAdjust: () => void;
  onSubstitutes: () => void;
  onDelete: () => void;
}

export function MedicineRow({ medicine, onEdit, onRestock, onAdjust, onSubstitutes, onDelete }: MedicineRowProps) {
  return (
    <Card className="flex items-center gap-4 p-3.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate font-medium">{medicine.name}</p>
          <ExpiryBadge daysToExpiry={medicine.daysToExpiry} />
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {medicine.genericName || medicine.composition || medicine.category}
          {' · '}Batch {medicine.batchNo}
          {medicine.supplier && ` · ${medicine.supplier.name}`}
        </p>
      </div>

      <div className="hidden shrink-0 text-right sm:block">
        <p className="tabular text-sm font-semibold">{money(medicine.price)}</p>
        {medicine.costPrice != null && (
          <p className="tabular text-xs text-muted-foreground">cost {money(medicine.costPrice)}</p>
        )}
      </div>

      <div className="shrink-0">
        <StockBadge status={medicine.stockStatus} stock={medicine.stock} unit={medicine.unit} />
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Can permission={PERMISSIONS.medicinesAdjustStock}>
          <Button variant="outline" size="sm" onClick={onRestock} className="hidden md:inline-flex">
            <PlusCircle /> Restock
          </Button>
        </Can>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Can permission={PERMISSIONS.medicinesEdit}>
              <DropdownMenuItem onClick={onEdit}>
                <Pencil /> Edit details
              </DropdownMenuItem>
            </Can>
            <Can permission={PERMISSIONS.medicinesAdjustStock}>
              <DropdownMenuItem onClick={onRestock} className="md:hidden">
                <PlusCircle /> Restock
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onAdjust}>
                <Sliders /> Adjust stock
              </DropdownMenuItem>
            </Can>
            <DropdownMenuItem onClick={onSubstitutes}>
              <Link2 /> Substitutes
            </DropdownMenuItem>
            <Can permission={PERMISSIONS.medicinesDelete}>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-status-expired focus:bg-status-expired/10 focus:text-status-expired">
                <Trash2 /> Delete
              </DropdownMenuItem>
            </Can>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}

export function MedicineRowSkeletonList() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="flex items-center gap-4 p-3.5">
          <Boxes className="h-4 w-4 shrink-0 text-transparent" />
          <div className="h-4 flex-1 animate-pulse rounded bg-secondary" />
          <div className="h-4 w-20 animate-pulse rounded bg-secondary" />
        </Card>
      ))}
    </div>
  );
}
